import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { SeoData } from "../../types/serviceDetail";


interface SEOProps {
    seoData: SeoData;
}
const SeoMeta: React.FC<SEOProps> = ({ seoData }) => {
    if (!seoData.seoSettings) return null;
    useEffect(() => {
        if (seoData?.thirdPartyScripts) {
             // Filter head scripts
            const headScripts = seoData.thirdPartyScripts.filter(
                script => script.script_position === "head"
            );
               
            const scriptElements = headScripts.map(script => {
                const scriptTag = document.createElement("script");
                scriptTag.type = (script.script_type === 'JSON') ? "application/ld+json" : script.script_type;

                // If the string contains <script>, strip tags and keep only content
                const code = script.script_code
                    ?.replace(/<\/?script[^>]*>/g, "") // remove <script> tags
                    .trim();

                if (code) {
                    scriptTag.innerHTML = code;
                }

                document.head.appendChild(scriptTag);
                return scriptTag;
            });
            return () => {
                scriptElements.forEach(el => {
                    if (document.head.contains(el)) {
                        document.head.removeChild(el);
                    }
                });
            };
        }
    }, [seoData]);

    const { seoSettings, metaTags, ogTags, twitterTags, thirdPartyScripts } = seoData;

    return (
        <>
            <Helmet>
                <title>{seoSettings.title}</title>
                {seoSettings?.description && (
                    <meta name="description" content={seoSettings.description} />
                )}
                {seoSettings?.keywords && (
                    <meta name="keywords" content={seoSettings.keywords} />
                )}
                {seoSettings?.canonical_url && (
                    <link rel="canonical" href={seoSettings.canonical_url} />
                )}
                {seoSettings?.robots && (
                    <meta name="robots" content={seoSettings.robots} />
                )}

                {metaTags?.map((meta, index) => (
                    <meta key={index} name={meta.meta_name} content={meta.meta_content} />
                ))}

                {ogTags?.og_title && (
                    <meta property="og:title" content={ogTags.og_title} />
                )}
                {ogTags?.og_type && (
                    <meta property="og:type" content={ogTags.og_type} />
                )}
                {ogTags?.og_url && (
                    <meta property="og:url" content={ogTags.og_url} />
                )}
                {ogTags?.og_image && (
                    <meta property="og:image" content={ogTags.og_image} />
                )}
                {ogTags?.og_site_name && (
                    <meta property="og:site_name" content={ogTags.og_site_name} />
                )}
                {ogTags?.og_description && (
                    <meta property="og:description" content={ogTags.og_description} />
                )}
                {twitterTags?.twitter_card && (
                    <meta name="twitter:card" content={twitterTags.twitter_card} />
                )}
                {twitterTags?.twitter_title && (
                    <meta name="twitter:title" content={twitterTags.twitter_title} />
                )}
                {twitterTags?.twitter_description && (
                    <meta name="twitter:description" content={twitterTags.twitter_description} />
                )}
                {twitterTags?.twitter_image && (
                    <meta name="twitter:image" content={twitterTags.twitter_image} />
                )}
                {twitterTags?.twitter_site && (
                    <meta name="twitter:site" content={twitterTags.twitter_site} />
                )}

                {/* Add head scripts */}
                {thirdPartyScripts
                    .filter(script => script.script_position === "body")
                    .map((script, index) => (
                        <script key={index} type={script.script_type} dangerouslySetInnerHTML={{ __html: script.script_code }} />
                    ))}
            </Helmet>
        </>
    );
};

export default SeoMeta;
